// Real /statements/:id viewer — GET /api/v1/statements/<id>. Renders the
// summary_json the period-close service writes. Per-statement Holdings +
// Activity tables are deferred to a later slice (will need their own
// endpoints scoped to the period).

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Brand, Kicker } from '../../components/shell';
import { fmtBase, fmtPct, fmtQty } from '../../lib/format';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

const SUPERSEDE_REASONS = [
  ['data_correction', 'Data correction'],
  ['pricing_correction', 'Pricing correction'],
  ['fee_adjustment', 'Fee adjustment'],
  ['restatement', 'Restatement'],
  ['other', 'Other'],
] as const;

interface StatementDetail {
  id: string;
  client_id: string;
  client_name: string;
  period_label: string;
  period_starts: string;
  period_ends: string;
  version: number;
  issued_at: string | null;
  status_label: 'issued' | 'correction' | 'superseded';
  superseded: boolean;
  superseded_by_id: string | null;
  note: string;
  pdf_available?: boolean;
  // Phase 9.b — Fund statements may be issued in a non-USD base
  // currency (n8 EUR is the imminent case). Default 'USD' for SMA
  // statements; Fund statements override via summary_json.base_currency.
  base_currency?: string;
  summary: {
    opening_aum?: number;
    ending_aum?: number;
    net_flows?: number;
    gross_pnl?: number;
    fees?: number;
    period_return_pct?: number;
    period_return_twr_pct?: number;
    net_period_return_twr_pct?: number;
    net_return?: number;
    net_return_pct?: number;
    ytd_return_pct?: number;
    itd_return_pct?: number;
    realized_gains?: number;
    unrealized_gains_start?: number;
    unrealized_gains_end?: number;
    change_in_unrealized?: number;
    trading_fees?: number;
    management_fees?: number;
    expense_ratio_annual_pct?: number;
    snapshot_as_of?: string | null;
    recon_status?: string;
    inception_date?: string | null;
  };
  holdings_end?: Holding[];
  ledger?: LedgerRow[];
  accounts?: Acct[];
}

interface Holding {
  asset: string; quantity: number; avg_cost_usd: number; market_price_usd: number;
  market_value_usd: number; unrealized_pnl_usd: number; is_cash: boolean;
}
interface LedgerRow {
  occurred_at: string; tx_type: string; asset: string; quantity: number;
  price_usd: number | null; usd_value: number; reference: string;
}
interface Acct {
  label: string; venue: string; chain?: string; ownership: string;
}

const VENUE_LABELS: Record<string, string> = {
  coinbase_exchange: 'Coinbase Exchange', bybit: 'Bybit', kraken: 'Kraken',
  onchain: 'On-chain',
};
const CHAIN_LABELS: Record<string, string> = { ethereum: 'Ethereum', bsc: 'BNB Smart Chain' };
const venueLabel = (v: string, chain?: string) =>
  v === 'onchain' && chain && CHAIN_LABELS[chain] ? CHAIN_LABELS[chain] : (VENUE_LABELS[v] || v);
const ownershipLabel = (o: string) => (o === 'client_owned' ? 'Client-owned' : 'Company-held');
const TX_LABELS: Record<string, string> = {
  deposit: 'Deposit', withdrawal: 'Withdrawal', trade_buy: 'Buy', trade_sell: 'Sell',
  fee: 'Fee', transfer_in: 'Transfer in', transfer_out: 'Transfer out', adjustment: 'Adjustment',
};
const fmtUTC = (iso: string | null | undefined) =>
  iso ? new Date(iso).toISOString().slice(0, 19).replace('T', ' ') + ' UTC' : '—';

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  }) : '—';

export default function StatementViewer() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [stmt, setStmt] = useState<StatementDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [correcting, setCorrecting] = useState(false);
  const [reason, setReason] = useState<string>('data_correction');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [correctErr, setCorrectErr] = useState<string | null>(null);

  const submitCorrection = async () => {
    setSubmitting(true); setCorrectErr(null);
    try {
      const resp = await api<{ statement_id: string }>(`/ops/statements/${id}/supersede`, {
        method: 'POST', body: { reason, note },
      });
      navigate(`/statements/${resp.statement_id}${window.location.search}`);
      setCorrecting(false);
    } catch (e) {
      setCorrectErr((e as { message?: string })?.message || 'Correction failed.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    api<StatementDetail>(`/statements/${id}`)
      .then(s => { if (!cancelled) setStmt(s); })
      .catch(() => { if (!cancelled) setError('Statement not found.'); });
    return () => { cancelled = true; };
  }, [id]);

  if (error) {
    return (
      <div className="view container">
        <button className="btn-link" onClick={() => navigate('/statements')}>← All statements</button>
        <p className="serif-body" style={{ color: 'var(--accent)', marginTop: 24 }}>{error}</p>
      </div>
    );
  }
  if (!stmt) {
    return (
      <div className="view container">
        <p style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
          Loading…
        </p>
      </div>
    );
  }

  const s = stmt.summary || {};
  // Phase 9.b — currency-aware formatting. Defaults to USD for SMA
  // statements; Fund statements override via backend base_currency.
  const ccy = stmt.base_currency || 'USD';
  const fmt = (n: number) => fmtBase(n, ccy);

  return (
    <div className="view container" style={{ paddingBottom: 96 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '16px 0', borderBottom: 'var(--hairline)', marginBottom: 56,
      }}>
        <button className="btn-link" onClick={() => navigate('/statements')}>← All statements</button>
        <div style={{ display: 'flex', gap: 16 }}>
          {user?.role === 'manager' && !stmt.superseded && (
            <button className="btn-ghost btn" onClick={() => setCorrecting(v => !v)}>Issue correction</button>
          )}
          <button className="btn-ghost btn" onClick={() => window.print()}>Print</button>
          {stmt.pdf_available === false ? (
            <span
              className="btn btn-ghost"
              title="The PDF is being rendered. Try again in a few minutes."
              style={{
                display: 'inline-flex', alignItems: 'center',
                color: 'var(--ink-3)', cursor: 'default',
              }}
            >
              PDF rendering · refresh in 30 min
            </span>
          ) : (
            <a className="btn"
               href={`/api/v1/statements/${stmt.id}/pdf`}
               download
               style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
              Download PDF
            </a>
          )}
        </div>
      </div>

      {correcting && (
        <div style={{
          border: '1px solid var(--ink)', padding: '20px 24px', marginBottom: 40,
          display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 640,
        }}>
          <div className="label">ISSUE CORRECTION</div>
          <p className="serif-body" style={{ margin: 0, fontSize: 14, color: 'var(--ink-2)' }}>
            Recomputes this period from current data and issues a new version that
            supersedes — but does not replace — v{stmt.version}. The original stays on record.
          </p>
          <label className="field">
            <span className="field-label">REASON</span>
            <select className="field-input" value={reason} onChange={e => setReason(e.target.value)}>
              {SUPERSEDE_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label className="field">
            <span className="field-label">NOTE (OPTIONAL)</span>
            <textarea className="field-input" value={note} onChange={e => setNote(e.target.value)}
                      placeholder="What changed and why" />
          </label>
          {correctErr && <p className="serif-body" style={{ color: 'var(--oxblood)', margin: 0, fontSize: 14 }}>{correctErr}</p>}
          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn" disabled={submitting} onClick={submitCorrection}>
              {submitting ? 'Issuing…' : 'Issue correction'}
            </button>
            <button className="btn-link" onClick={() => setCorrecting(false)}>Cancel</button>
          </div>
        </div>
      )}

      <header style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32,
        paddingBottom: 48, borderBottom: '1px solid var(--ink)',
      }}>
        <div>
          <Brand onClick={() => navigate('/dashboard')} />
          <div style={{ marginTop: 56 }}>
            <Kicker>CLIENT</Kicker>
            <div className="display-m" style={{ fontSize: 36, marginTop: 0 }}>{stmt.client_name}</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Kicker>{stmt.period_label}</Kicker>
          <div className="display-m" style={{ fontSize: 36 }}>v{stmt.version}</div>
          <div className="serif-body" style={{ fontStyle: 'italic', color: 'var(--ink-2)', marginTop: 16 }}>
            Issued {fmtDate(stmt.issued_at)}
          </div>
          {stmt.note && (
            <div className="serif-body" style={{ fontStyle: 'italic', color: 'var(--accent)', marginTop: 8, fontSize: 14 }}>
              {stmt.note}
            </div>
          )}
          {stmt.superseded && (
            <div className="label-s" style={{ marginTop: 12, color: 'var(--accent)' }}>
              SUPERSEDED BY A LATER VERSION
            </div>
          )}
          {/* Phase 9.b LOW 12 — surface correction status in the detail */}
          {/* header (was only visible in the list view).                  */}
          {stmt.status_label === 'correction' && !stmt.superseded && (
            <div className="label-s" style={{ marginTop: 12, color: 'var(--accent)' }}>
              CORRECTION · VERSION {stmt.version}
            </div>
          )}
        </div>
      </header>

      <section style={{ marginTop: 40 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24 }}>
          <Meta label="PERIOD (UTC)" value={`${fmtUTC(stmt.period_starts)} → ${fmtUTC(stmt.period_ends)}`} />
          <Meta label="VALUED AS OF" value={fmtUTC(s.snapshot_as_of)} />
          <Meta label="RECONCILIATION" value={s.recon_status === 'ok' ? 'Reconciled' : (s.recon_status || '—')} />
          <Meta label="ACCOUNT OPENED" value={fmtDate(s.inception_date ?? null)} />
        </div>
      </section>

      <section style={{ marginTop: 48 }}>
        <Kicker>PERIOD SUMMARY</Kicker>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '36px 80px', marginTop: 32,
        }}>
          <SummaryRow label="Opening AUM" value={fmt(s.opening_aum ?? 0)} />
          <SummaryRow label="Ending AUM" value={fmt(s.ending_aum ?? 0)} accent />
          <SummaryRow label="Net cash flows" value={fmt(s.net_flows ?? 0)} />
          <SummaryRow label="Gross P&L" value={fmt(s.gross_pnl ?? 0)} />
          <SummaryRow label="Fees" value={fmt(s.fees ?? 0)} />
          <SummaryRow
            label="Net P&L (after fees)"
            value={fmt(s.net_return ?? ((s.gross_pnl ?? 0) - (s.fees ?? 0)))}
            accent
          />
        </div>
      </section>

      <section style={{ marginTop: 56 }}>
        <Kicker>RETURNS</Kicker>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '36px 80px', marginTop: 32 }}>
          <SummaryRow label="Gross return (period)" value={fmtPct((s.period_return_pct ?? 0))} />
          <SummaryRow label="Net return (period, after fees)"
            value={fmtPct((s.net_return_pct ?? 0))} accent />
          {s.period_return_twr_pct !== undefined && (
            <SummaryRow label="Time-weighted return (gross)" value={fmtPct(s.period_return_twr_pct)} />
          )}
          {s.net_period_return_twr_pct !== undefined && (
            <SummaryRow label="Time-weighted return (net)" value={fmtPct(s.net_period_return_twr_pct)} accent />
          )}
          {s.ytd_return_pct !== undefined && (
            <SummaryRow label="Year-to-date (gross)" value={fmtPct(s.ytd_return_pct)} />
          )}
          {s.itd_return_pct !== undefined && (
            <SummaryRow label="Since inception (gross)" value={fmtPct(s.itd_return_pct)} />
          )}
        </div>
        <p className="serif-body" style={{ marginTop: 16, fontSize: 13, fontStyle: 'italic', color: 'var(--ink-3)', maxWidth: '72ch' }}>
          Net figures are after all fees. The time-weighted return (Modified Dietz) is the primary performance measure; year-to-date and since-inception compound the periodic returns of issued statements.
        </p>
      </section>

      {s.trading_fees !== undefined && (
        <section style={{ marginTop: 56 }}>
          <Kicker>FEE BREAKDOWN</Kicker>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '36px 80px', marginTop: 32,
          }}>
            <SummaryRow label="Trading fees" value={fmt(s.trading_fees ?? 0)} />
            <SummaryRow label="Management fees" value={fmt(s.management_fees ?? 0)} />
            <SummaryRow
              label="Annualized expense ratio"
              value={fmtPct((s.expense_ratio_annual_pct ?? 0))}
            />
          </div>
          <p className="serif-body" style={{ marginTop: 16, fontSize: 13, fontStyle: 'italic', color: 'var(--ink-3)', maxWidth: '70ch' }}>
            Trading fees are exchange commissions and on-chain gas paid during the period. Management fees are Rev9's advisory billing posted by ops. Annualized expense ratio scales management fees to a 12-month basis over average AUM for the period.
          </p>
        </section>
      )}

      {(s.realized_gains !== undefined || s.change_in_unrealized !== undefined) && (
        <section style={{ marginTop: 56 }}>
          <Kicker>P&L BREAKDOWN</Kicker>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '36px 80px', marginTop: 32,
          }}>
            <SummaryRow label="Realized gains" value={fmt(s.realized_gains ?? 0)} />
            <SummaryRow label="Change in unrealized" value={fmt(s.change_in_unrealized ?? 0)} />
          </div>
          <p className="serif-body" style={{ marginTop: 16, fontSize: 13, fontStyle: 'italic', color: 'var(--ink-3)', maxWidth: '70ch' }}>
            Realized gains are sale proceeds less FIFO cost basis on positions sold during the period. Change in unrealized is mark-to-market drift on positions held across the period boundary. The two sum to gross P&L.
          </p>
        </section>
      )}

      {stmt.holdings_end && stmt.holdings_end.filter(h => h.market_value_usd > 0).length > 0 && (
        <section style={{ marginTop: 56 }}>
          <Kicker>HOLDINGS AT PERIOD END</Kicker>
          <table className="tbl" style={{ marginTop: 16 }}>
            <thead><tr>
              <th>ASSET</th><th data-align="right">QUANTITY</th><th data-align="right">AVG COST</th>
              <th data-align="right">PRICE</th><th data-align="right">MARKET VALUE</th><th data-align="right">UNREALIZED P&L</th>
            </tr></thead>
            <tbody>
              {stmt.holdings_end.filter(h => h.market_value_usd > 0).map(h => (
                <tr key={h.asset}>
                  <td>{h.asset}</td>
                  <td data-align="right" className="num">{fmtQty(h.quantity, h.is_cash ? 2 : 6)}</td>
                  <td data-align="right" className="num">{h.avg_cost_usd ? fmt(h.avg_cost_usd) : '—'}</td>
                  <td data-align="right" className="num">{h.market_price_usd ? fmt(h.market_price_usd) : '—'}</td>
                  <td data-align="right" className="num">{fmt(h.market_value_usd)}</td>
                  <td data-align="right" className={'num ' + (h.unrealized_pnl_usd < 0 ? 'neg' : 'pos')}>{fmt(h.unrealized_pnl_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(() => {
            const n = (stmt.holdings_end || []).filter(h => h.market_value_usd <= 0).length;
            return n > 0 ? (
              <p className="serif-body" style={{ marginTop: 12, fontSize: 13, fontStyle: 'italic', color: 'var(--ink-3)' }}>
                Plus {n} position{n === 1 ? '' : 's'} held with no available market price (carried at $0.00).
              </p>
            ) : null;
          })()}
        </section>
      )}

      {stmt.ledger && stmt.ledger.length > 0 && (
        <section style={{ marginTop: 56 }}>
          <Kicker>TRANSACTION LEDGER</Kicker>
          <table className="tbl" style={{ marginTop: 16 }}>
            <thead><tr>
              <th>DATE (UTC)</th><th>TYPE</th><th>ASSET</th>
              <th data-align="right">QUANTITY</th><th data-align="right">PRICE</th><th data-align="right">USD VALUE</th>
            </tr></thead>
            <tbody>
              {stmt.ledger.map((t, i) => (
                <tr key={i}>
                  <td className="num" style={{ fontSize: 12 }}>{t.occurred_at.slice(0, 16).replace('T', ' ')}</td>
                  <td>{TX_LABELS[t.tx_type] || t.tx_type}</td>
                  <td>{t.asset}</td>
                  <td data-align="right" className="num">{fmtQty(t.quantity, ['USD','USDC','USDT'].includes(t.asset) ? 2 : 6)}</td>
                  <td data-align="right" className="num">{t.price_usd != null ? fmt(t.price_usd) : '—'}</td>
                  <td data-align="right" className="num">{fmt(t.usd_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section style={{ marginTop: 64 }}>
        <Kicker>METHODOLOGY &amp; BASIS OF PREPARATION</Kicker>
        <div className="serif-body" style={{ marginTop: 16, fontSize: 15, color: 'var(--ink-2)', maxWidth: '72ch' }}>
          <p>
            Prepared on a mark-to-market basis. Positions are valued at venue spot prices (Coinbase Exchange) as of the valuation timestamp above; stablecoins are carried at par. Period boundaries are UTC. Net P&L = Ending AUM − Opening AUM − Net cash flows − Fees. Cost basis is computed FIFO per account from transaction history; cash carries basis at par. The time-weighted return is Modified Dietz, weighting each cash flow by the fraction of the period it was invested. Figures are unaudited and subject to final reconciliation.
          </p>
        </div>
      </section>

      {stmt.accounts && stmt.accounts.length > 0 && (
        <section style={{ marginTop: 48 }}>
          <div className="label" style={{ marginBottom: 12 }}>CUSTODY</div>
          <p className="serif-body" style={{ margin: 0, fontSize: 15, maxWidth: '72ch', color: 'var(--ink-2)' }}>
            Assets are held at the following venues, which act as custodian for the positions shown:{' '}
            {stmt.accounts.map((a, i) => (
              <span key={i}>{(a.label || venueLabel(a.venue, a.chain))} ({venueLabel(a.venue, a.chain)}, {ownershipLabel(a.ownership)}){i < (stmt.accounts!.length - 1) ? '; ' : ''}</span>
            ))}. Rev9 Capital LLC maintains view-only access for reporting and reconciliation and does not have withdrawal authority over client venue keys.
          </p>
        </section>
      )}

      <section style={{ marginTop: 48 }}>
        <div className="label" style={{ marginBottom: 12 }}>IMPORTANT INFORMATION</div>
        <p className="serif-body" style={{ margin: 0, fontSize: 13, maxWidth: '92ch', color: 'var(--ink-3)', lineHeight: 1.6 }}>
          This statement is a final record for the period and is not subject to revision. Corrections, if necessary, are issued as a new statement that supersedes — but does not replace — the original. <strong style={{ color: 'var(--ink-2)' }}>Digital assets are not bank deposits, are not FDIC- or SIPC-insured, are not guaranteed, and may lose value, including the total loss of principal.</strong> Digital-asset markets are highly volatile. Past performance does not guarantee future results. This statement is for informational purposes, is not an offer or solicitation, and is not tax, legal, or investment advice; clients should consult their own tax advisor. Prepared exclusively for the account owner named above; confidential.
        </p>
      </section>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="label-s" style={{ color: 'var(--ink-3)' }}>{label}</span>
      <span className="num" style={{ fontSize: 12 }}>{value}</span>
    </div>
  );
}

function SummaryRow({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: 'var(--hairline)', paddingBottom: 16 }}>
      <span className="label-s" style={{ color: 'var(--ink-3)' }}>{label}</span>
      <span
        className="num"
        style={{
          fontSize: accent ? 28 : 20,
          fontFamily: accent ? 'var(--serif)' : 'var(--mono)',
          fontStyle: accent ? 'italic' : 'normal',
          color: accent ? 'var(--accent)' : 'var(--ink)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </div>
  );
}
